import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Spin, Typography, Alert as AlertAntd } from "antd";
import { ConnectedProps, connect } from "react-redux";
import { useWebServer } from "../../../providers";
import { clearAlerts, listAlerts, updateAlerts } from "../../../actions";
import { ReducerState, JoinedAlert } from "../../../types";
import { useAppSelector } from "../../../hooks";
import { useListTrapsQuery } from "../../../actions/trapsiteInQuery";
import { useListDevicesQuery } from "../../../actions/devicesInQuery";
import { DownOutlined } from "@ant-design/icons";
import ArchiveBoxIcon from "@heroicons/react/24/outline/ArchiveBoxIcon";
import AlertDialog from "./AlertDialog";
import { TagColorMap } from "./const";
import "./index.css";

const { Text, Title } = Typography;

const defaultCheckedList = ["Info", "Danger", "Warning"];

const mapStateToProps = (state: ReducerState) => {
  return {
    selected: state.devices?.selected?.uuid || null,
  };
};

const connector = connect(mapStateToProps, {
  listAlerts,
  updateAlerts,
  clearAlerts,
});

type Props = ConnectedProps<typeof connector> & {
  // Extra props go here
};

const TrapAlert: React.FC<Props> = ({ selected, listAlerts }) => {
  const { sendRequest } = useWebServer();
  const [loading, setLoading] = useState(false);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const { alerts } = useAppSelector((state) => {
    const detectionAlerts = Object.values(state.alerts.alerts).filter(
      (item) => item.tag !== "Detection",
    );
    return {
      alerts: detectionAlerts,
    };
  });

  const { data: trapData, isLoading: trapLoading } = useListTrapsQuery();
  const { data: devices, isLoading: devicesLoading } = useListDevicesQuery();

  const joinedAlerts = useMemo(() => {
    return alerts.map((alert) => {
      const device = devices?.items?.find((d) => d.uuid === alert.device);
      const trap = trapData?.items?.find((t) => {
        return t._id === device?.trap_id;
      });
      return {
        ...alert,
        device_name: device?.display_name || "",
        trap_name: trap?.display_name || "",
      } as JoinedAlert;
    });
  }, [alerts, devices, trapData]);

  const offlineCount = useMemo(() => {
    return (
      devices?.items?.filter((device) => device.status !== "idle")?.length || 0
    );
  }, [devices]);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    await listAlerts(sendRequest, {
      page: 0,
      pageSize: 100,
      device: selected || undefined,
      tags: Object.keys(TagColorMap),
      levels: defaultCheckedList,
      buildEnv: process.env.REACT_APP_ENV || "production",
    });
    setLoading(false);
  }, [listAlerts, sendRequest, selected]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return (
    <div className="detection-box-container">
      <Spin spinning={trapLoading || devicesLoading || loading}>
          <div className="detection-box-title">
          <Title level={4} className="detection-box-title-text">
            Traps
          </Title>
          <DownOutlined className="detection-box-title-icon" />
        </div>
        <div
          ref={listContainerRef}
          className={`detection-box-alerts ${
            joinedAlerts.length > 0 ? "has-alerts" : "no-alerts"
          }`}
        >
          <AlertAntd
            message={
              <Text strong>
                {offlineCount === 0
                  ? "All Device Online"
                  : `${offlineCount} Device Off`}
              </Text>
            }
            showIcon
            type={offlineCount === 0 ? "success" : "error"}
            icon={<ArchiveBoxIcon width={24} height={24} />}
            className="status-devices"
          />
          {joinedAlerts.length > 0 &&
            joinedAlerts.map((item: JoinedAlert) => (
              <AlertDialog
                key={item._id}
                item={item}
                //   onClick={doUpdateAlert}
              />
            ))}
        </div>
      </Spin>
    </div>
  );
};

export default connector(TrapAlert);
